import { BusinessHoursResponseDto } from '@tiendanube/common';

export interface ExtendedBusinessHoursResponseDto
  extends BusinessHoursResponseDto {
  index: number;
}

export const WEEKDAYS_OPTIONS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const START_DEFAULT = '09:00';
export const END_DEFAULT = '18:00';

export const formatBusinessHourErrors = (
  errors: Partial<Record<string, string>>,
  businessHours: ExtendedBusinessHoursResponseDto[],
): Record<string, string> => {
  const formattedErrors: Record<string, string> = {};
  const regex = /businessHours\[(\d+)\]\.(\w+)/;

  Object.keys(errors).forEach((key) => {
    if (!key.startsWith('businessHours')) {
      formattedErrors[key] = errors[key] ?? '';
      return;
    }

    // Handle global businessHours array error (e.g., min length validation)
    if (key === 'businessHours') {
      formattedErrors[key] = errors[key] ?? '';
      return;
    }

    const match = regex.exec(key);
    if (!match || !errors[key]) {
      return;
    }

    const index = parseInt(match[1], 10);
    const field = match[2];

    const businessHour = businessHours[index];
    if (businessHour) {
      formattedErrors[`${businessHour.day}.${index}.${field}`] =
        errors[key] ?? '';
      return;
    }

    formattedErrors[`${index}.${field}`] = errors[key] ?? '';
  });

  return formattedErrors;
};

export const sortBusinessHours = (
  businessHours: ExtendedBusinessHoursResponseDto[],
) => {
  const sortedHours = [...businessHours].sort((a, b) => {
    const indexA = WEEKDAYS_OPTIONS.indexOf(a.day);
    const indexB = WEEKDAYS_OPTIONS.indexOf(b.day);

    if (indexA !== indexB) {
      return indexA - indexB;
    }

    const indexAValue = a.index ?? 0;
    const indexBValue = b.index ?? 0;
    return indexAValue - indexBValue;
  });

  return sortedHours.map((bh, index) => ({
    ...bh,
    index,
  }));
};

export const getBusinessHoursWithIndex = (
  businessHours: BusinessHoursResponseDto[],
): ExtendedBusinessHoursResponseDto[] => {
  if (businessHours.length > 0 && 'index' in businessHours[0]) {
    return businessHours as ExtendedBusinessHoursResponseDto[];
  }

  return businessHours.map((bh, index) => ({
    ...bh,
    index,
  })) as ExtendedBusinessHoursResponseDto[];
};
